/**
 * Profile validation shared by the website and the Worker. The server always re-checks — the
 * browser runs the same rules only to show problems early.
 */
import type { AvatarCfg, Prefs, ProfileInput } from "./api-types.ts";

export const NAME_MAX = 24;
export const PARTY_MAX = 32;
export const AVATAR_MAX_BYTES = 300 * 1024;
export const AVATAR_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;

const LOOKS = ["man", "woman"];
const HAIR = ["short", "swept", "long", "curly", "bun", "bald"];
const FACIAL = ["none", "moustache", "handlebar", "beard"];
const EXTRAS = ["none", "glasses", "hat", "hatglasses"];
const SKIN_TONES = 6;
const HAIR_COLOURS = 7;

/** Trim, collapse spaces, drop control characters. */
function clean(s: string): string {
  return s.replace(/[\u0000-\u001f\u007f]/g, "").replace(/\s+/g, " ").trim();
}

export function cleanName(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const s = clean(v);
  return s.length >= 1 && s.length <= NAME_MAX ? s : null;
}

export function cleanAvatar(v: unknown): AvatarCfg | null {
  if (!v || typeof v !== "object") return null;
  const a = v as Record<string, unknown>;
  const int = (x: unknown, n: number) => Number.isInteger(x) && (x as number) >= 0 && (x as number) < n;
  if (!LOOKS.includes(a.look as string) || !int(a.skin, SKIN_TONES) || !HAIR.includes(a.hair as string) ||
      !int(a.hairCol, HAIR_COLOURS) || !FACIAL.includes(a.facial as string) || !EXTRAS.includes(a.extra as string)) return null;
  return { look: a.look as AvatarCfg["look"], skin: a.skin as number, hair: a.hair as string, hairCol: a.hairCol as number,
    facial: a.facial as string, extra: a.extra as string };
}

export function cleanPrefs(v: unknown): Prefs | null {
  if (!v || typeof v !== "object") return null;
  const p = v as Record<string, unknown>, out: Prefs = {};
  if (p.party !== undefined) { if (typeof p.party !== "string") return null; const s = clean(p.party); if (s.length > PARTY_MAX) return null; if (s) out.party = s; }
  if (p.colour !== undefined) { if (typeof p.colour !== "string" || !/^#[0-9a-f]{6}$/i.test(p.colour)) return null; out.colour = p.colour.toLowerCase(); }
  if (p.theme !== undefined) { if (typeof p.theme !== "string" || !/^[a-z0-9-]{1,40}$/.test(p.theme)) return null; out.theme = p.theme; }
  return out;
}

/** Check a create/edit body. Returns the cleaned input, or the first problem found. */
export function checkProfileInput(v: unknown): { ok: true; value: ProfileInput } | { ok: false; message: string } {
  if (!v || typeof v !== "object" || Array.isArray(v)) return { ok: false, message: "Expected a JSON object." };
  const b = v as Record<string, unknown>, out: ProfileInput = {};
  for (const k of Object.keys(b)) if (!["displayName", "avatar", "prefs"].includes(k)) return { ok: false, message: `Unknown field: ${k}` };
  if (b.displayName !== undefined) {
    const n = cleanName(b.displayName);
    if (!n) return { ok: false, message: `Display name must be 1–${NAME_MAX} characters.` };
    out.displayName = n;
  }
  if (b.avatar !== undefined) {
    if (b.avatar === null) out.avatar = null;
    else { const a = cleanAvatar(b.avatar); if (!a) return { ok: false, message: "Avatar settings are not valid." }; out.avatar = a; }
  }
  if (b.prefs !== undefined) {
    const p = cleanPrefs(b.prefs);
    if (!p) return { ok: false, message: "Preferences are not valid." };
    out.prefs = p;
  }
  return { ok: true, value: out };
}
